import { api, type QueryParams } from "@/lib/api/client";
import type { Page } from "@/lib/api/types";
import type {
  BalanceEntry,
  ChecklistItem,
  ExtensionPreview,
  Handover,
  LeaveBalance,
  LeaveChecklist,
  LeaveDuration,
  LeaveExtension,
  LeavePreview,
  LeaveRequest,
  LeaveSettings,
  LeaveType,
  PublicHoliday,
  Returning,
} from "@/features/leave/types";

export const leaveKeys = {
  all: ["leave"] as const,
  types: () => [...leaveKeys.all, "types"] as const,
  holidays: (year?: number) => [...leaveKeys.all, "holidays", year ?? "all"] as const,
  myBalances: (year: number) => [...leaveKeys.all, "my-balances", year] as const,
  myRequests: () => [...leaveKeys.all, "my-requests"] as const,
  queue: (filters: Record<string, unknown>) => [...leaveKeys.all, "queue", filters] as const,
  balances: (userId: string, year: number) =>
    [...leaveKeys.all, "balances", userId, year] as const,
  entries: (userId: string, year: number) =>
    [...leaveKeys.all, "entries", userId, year] as const,
  handover: (requestId: string) => [...leaveKeys.all, "handover", requestId] as const,
  myHandovers: () => [...leaveKeys.all, "my-handovers"] as const,
  checklist: (requestId: string) => [...leaveKeys.all, "checklist", requestId] as const,
  returning: () => [...leaveKeys.all, "returning"] as const,
  extensions: () => [...leaveKeys.all, "extensions"] as const,
  settings: () => [...leaveKeys.all, "settings"] as const,
};

export interface PreviewInput {
  leave_type_id: string;
  start_date: string;
  end_date: string;
  has_attachment?: boolean;
  reason_code?: string | null;
  user_id?: string;
  duration?: LeaveDuration | null;
  hours?: string | null;
}

export const leaveApi = {
  types: (includeInactive = false) =>
    api.get<LeaveType[]>("/leave-types", { query: { include_inactive: includeInactive } }),

  createType: (payload: Record<string, unknown>) => api.post<LeaveType>("/leave-types", payload),

  updateType: (id: string, payload: Record<string, unknown>) =>
    api.patch<LeaveType>(`/leave-types/${id}`, payload),

  deactivateType: (id: string) => api.delete<{ message: string }>(`/leave-types/${id}`),

  holidays: (year?: number) =>
    api.get<PublicHoliday[]>("/public-holidays", { query: year ? { year } : undefined }),

  createHoliday: (payload: Record<string, unknown>) =>
    api.post<PublicHoliday>("/public-holidays", payload),

  updateHoliday: (id: string, payload: Record<string, unknown>) =>
    api.patch<PublicHoliday>(`/public-holidays/${id}`, payload),

  deleteHoliday: (id: string) => api.delete<{ message: string }>(`/public-holidays/${id}`),

  myBalances: (year: number) =>
    api.get<LeaveBalance[]>("/me/leave-balances", { query: { year } }),

  myRequests: () => api.get<LeaveRequest[]>("/me/leave-requests"),

  preview: (input: PreviewInput) => api.post<LeavePreview>("/me/leave-requests/preview", input),

  adminPreview: (input: PreviewInput) => api.post<LeavePreview>("/leave-requests/preview", input),

  request: (payload: Record<string, unknown>) =>
    api.post<LeaveRequest>("/me/leave-requests", payload),

  uploadAttachment: (file: File) => {
    const body = new FormData();
    body.append("file", file);
    return api.post<{ file_id: string }>("/me/leave-requests/attachment", body);
  },

  cancelMine: (id: string) => api.post<{ message: string }>(`/me/leave-requests/${id}/cancel`),

  queue: (filters: QueryParams) =>
    api.get<Page<LeaveRequest>>("/leave-requests", { query: filters }),

  approve: (id: string, comment?: string, overrideWarnings = false, coverFromOvertime = false) =>
    api.post<LeaveRequest>(`/leave-requests/${id}/approve`, {
      comment: comment ?? null,
      override_warnings: overrideWarnings,
      cover_from_overtime: coverFromOvertime,
    }),

  reject: (id: string, comment: string) =>
    api.post<LeaveRequest>(`/leave-requests/${id}/reject`, { comment }),

  cancel: (id: string) => api.post<{ message: string }>(`/leave-requests/${id}/cancel`),

  balancesFor: (userId: string, year: number) =>
    api.get<LeaveBalance[]>(`/leave-balances/${userId}`, { query: { year } }),

  entriesFor: (userId: string, year: number) =>
    api.get<BalanceEntry[]>(`/leave-balances/${userId}/entries`, { query: { year } }),

  adjustBalance: (payload: Record<string, unknown>) =>
    api.post<{ message: string }>("/leave-balances/adjust", payload),

  // ── Handover ───────────────────────────────────────────────────────────────

  handover: (requestId: string) =>
    api.get<Handover | null>(`/leave-requests/${requestId}/handover`),

  myHandovers: () => api.get<Handover[]>("/me/handovers"),

  writeHandover: (requestId: string, payload: { to_user_id: string; notes: string }) =>
    api.put<Handover>(`/leave-requests/${requestId}/handover`, payload),

  acceptHandover: (requestId: string) =>
    api.post<Handover>(`/leave-requests/${requestId}/handover/accept`),

  handBack: (requestId: string, notes: string) =>
    api.post<Handover>(`/leave-requests/${requestId}/handover/handback`, { notes }),

  closeHandover: (requestId: string) =>
    api.post<Handover>(`/leave-requests/${requestId}/handover/close`),

  // ── Staying away longer ────────────────────────────────────────────────────

  previewExtension: (requestId: string, endDate: string) =>
    api.post<ExtensionPreview>(`/me/leave-requests/${requestId}/extensions/preview`, {
      end_date: endDate,
    }),

  askExtension: (requestId: string, payload: { end_date: string; reason: string | null }) =>
    api.post<LeaveExtension>(`/me/leave-requests/${requestId}/extensions`, payload),

  withdrawExtension: (extensionId: string) =>
    api.post<{ message: string }>(`/me/leave-extensions/${extensionId}/cancel`),

  extensions: () => api.get<LeaveExtension[]>("/leave-extensions"),

  approveExtension: (id: string, comment?: string, overrideWarnings = false) =>
    api.post<LeaveExtension>(`/leave-extensions/${id}/approve`, {
      comment: comment ?? null,
      override_warnings: overrideWarnings,
    }),

  rejectExtension: (id: string, comment: string) =>
    api.post<LeaveExtension>(`/leave-extensions/${id}/reject`, { comment }),

  // ── Coming back ────────────────────────────────────────────────────────────

  returning: () => api.get<Returning[]>("/leave-returns"),

  confirmReturn: (
    requestId: string,
    payload: { returned_on: string; note: string | null; record_gap_as_unpaid: boolean },
  ) => api.post<LeaveRequest>(`/leave-requests/${requestId}/return`, payload),

  checklist: (requestId: string) =>
    api.get<LeaveChecklist>(`/leave-requests/${requestId}/checklist`),

  saveChecklist: (requestId: string, items: ChecklistItem[]) =>
    api.put<LeaveChecklist>(`/leave-requests/${requestId}/checklist`, { items }),

  settings: () => api.get<LeaveSettings>("/leave-settings"),

  saveSettings: (payload: LeaveSettings) => api.put<LeaveSettings>("/leave-settings", payload),

  runAccrual: (year: number) =>
    api.post<{ employees: number; credits: number; carry_overs: number }>(
      "/leave-balances/accrue",
      { year },
    ),
};

export interface Colleague {
  user_id: string;
  name: string;
  profile_picture_file_id: string | null;
}

/** Everybody else who works here, for naming a cover. */
export const peopleApi = {
  colleagues: (q?: string) => api.get<Colleague[]>("/me/colleagues", { query: { q } }),
};
